import React, {useMemo, useState} from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {debounce} from '../../../../../domain/lib/debounce';
import settingsParams from '../../../settingsParams';
import {useControllerProps} from '../../../../../domain/controllers/ControllerContext';
import {TextArea} from 'wix-ui-tpa';
import s from './BuyerNote.scss';
import {classes} from './BuyerNote.st.css';
import {useSettingsWithDefaults} from '../../../../../hooks/useSettingsWithDefaults';
import {TextButton, TextButtonPriority} from 'wix-ui-tpa/cssVars';
import {Document} from '@wix/wix-ui-icons-common';

export enum BuyerNoteDataHook {
  Root = 'BuyerNoteDataHook.root',
  Button = 'BuyerNoteDataHook.button',
  Body = 'BuyerNoteDataHook.body',
}

const DocumentIcon = (props: {className?: string}) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" {...props}>
      <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
        <g transform="translate(-515 -882)">
          <g transform="translate(515 882)">
            <path stroke="currentColor" d="M.5.5h7.778L11.5 3.737V12.5H.5V.5z"></path>
            <path stroke="currentColor" d="M10.793 3.5H8.5V1.207L10.793 3.5z"></path>
            <path fill="currentColor" d="M3 3H6V4H3z"></path>
            <path fill="currentColor" d="M3 6H9V7H3z"></path>
            <path fill="currentColor" d="M3 9H9V10H3z"></path>
          </g>
        </g>
      </g>
    </svg>
  );
};

const AddNoteButton = ({
  handleClick,
  caption,
  isOpen,
  refactorCartCouponSection,
}: {
  handleClick: () => void;
  caption: string;
  isOpen: boolean;
  refactorCartCouponSection: boolean;
}) => {
  return refactorCartCouponSection ? (
    <TextButton
      data-hook={BuyerNoteDataHook.Button}
      priority={TextButtonPriority.primary}
      className={classes.textButton}
      prefixIcon={<Document />}
      aria-expanded={isOpen}
      onClick={handleClick}>
      {caption}
    </TextButton>
  ) : (
    <button
      className={s.button}
      type="button"
      data-hook={BuyerNoteDataHook.Button}
      onClick={handleClick}
      aria-expanded={isOpen}>
      <DocumentIcon className={s.icon} />
      {caption}
    </button>
  );
};

const AddNoteBody = ({
  placeholder,
  handleChange,
  buyerNote,
}: {
  placeholder: string;
  handleChange: any;
  buyerNote: string;
}) => {
  const {t} = useTranslation();

  return (
    <div className={s.body}>
      <TextArea
        className={classes.textArea}
        data-hook={BuyerNoteDataHook.Body}
        placeholder={placeholder}
        autoFocus={true}
        maxLength={250}
        value={buyerNote || ''}
        onChange={handleChange}
        ariaLabel={t('cart.buyer_note_placeholder')}
      />
    </div>
  );
};

export const BuyerNote = () => {
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const {updateBuyerNote, sendAddNoteBi, refactorCartCouponSection} = useControllerProps().cartStore;
  const [buyerNote, setBuyerNote] = useState<string>(useControllerProps().cartStore.cartModel.buyerNote);
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(buyerNote));
  const debouncedUpdateBuyerNote = useMemo(() => debounce(updateBuyerNote), [updateBuyerNote]);

  const handleChange = ({target: {value}}: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBuyerNote(value);
    return debouncedUpdateBuyerNote(value);
  };

  const handleOpen = () => {
    !isOpen && sendAddNoteBi();
    setIsOpen(buyerNote ? true : !isOpen);
  };

  return (
    <div className={s.note} data-hook={BuyerNoteDataHook.Root}>
      <AddNoteButton
        isOpen={isOpen}
        handleClick={handleOpen}
        caption={getSettingsWithDefaults(settingsParams.CART_ADD_NOTE_LINK)}
        refactorCartCouponSection={refactorCartCouponSection}
      />
      {isOpen && (
        <AddNoteBody
          placeholder={getSettingsWithDefaults(settingsParams.CART_INSTRUCTIONS_TEXT)}
          handleChange={handleChange}
          buyerNote={buyerNote}
        />
      )}
    </div>
  );
};
